// ReviewItem.tsx
import { Glasses } from "@/components/button/cinemaButtons/glasses/Glasses";

import { IconBubbleText, IconCancel, IconClockCog, IconCrown } from "@tabler/icons-react";
import clsx from "clsx";

import ReplyIcon from "@/assets/icons/common/reply.svg?react";
import { DefaultObserveImage } from "@/components/image/DefaultObserveImage/DefaultObserveImage";
import { reviewsStore } from "@/stores/ReviewsStore";
import { Review } from "@/types/Reviews";
import { observer } from "mobx-react-lite";
import { useEffect, useRef } from "react";
import { ReviewRate } from "./Rate/ReviewRate";
import styles from "./Reviews.module.css";

const MAX_DEPTH = 5;

export const ReviewItem = observer(
  ({
    review,

    reply = true,
    depth = 0,
    replyAdmin = false,
    replyToName,
    titleRate,
  }: {
    review: Review;
    reply?: boolean;
    depth?: number;
    replyAdmin?: boolean;
    replyToName?: string | null;
    titleRate?: string;
  }) => {
    const repliesRef = useRef<HTMLDivElement>(null);

    const lineRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (!repliesRef.current || !lineRef.current) return;

      const repliesEl = repliesRef.current;
      const children = Array.from(repliesEl.children).filter(
        (el) => !el.classList.contains(styles.line),
      ) as HTMLElement[];

      if (children.length === 0) return;

      const lastChild = children[children.length - 1];
      const iconInLast = lastChild.querySelector(`.${styles.icon}`) as HTMLElement;

      if (!iconInLast) return;

      const repliesTop = repliesEl.getBoundingClientRect().top;
      const iconCenter = iconInLast.getBoundingClientRect().top + iconInLast.offsetHeight / 2;

      lineRef.current.style.height = `${iconCenter - repliesTop}px`;
    }, [review.replies]);

    // Собираем все ответы на максимальной глубине в плоский список
    const flattenReplies = (replies: Review[]): Review[] => {
      return replies.flatMap((r) => [r, ...flattenReplies(r.replies ?? [])]);
    };

    const isMaxDepth = depth >= MAX_DEPTH;

    return (
      <div className={styles.overlayReview} id={`review-${review.id}`}>
        <div className={clsx(styles.top, review.is_admin && styles.admin)}>
          <div className={styles.icon}>
            <div>
              {review.is_admin !== null ? (
                <DefaultObserveImage src_image="./logo.png" alt="" preview={false} />
              ) : (
                <Glasses />
              )}
            </div>
          </div>
          <div className={styles.topContent}>
            <section className={clsx(styles.name, "link-font !flex !flex-row !items-center")}>
              <span>{review.visitor_name}</span>
              {replyToName && !replyAdmin && <i className={styles.replyTo}>→ @{replyToName}</i>}
            </section>
            <span className={clsx(styles.date, "link-font")}>
              {review.created_at.replace(/(\d{4})-(\d{2})-(\d{2})/, "$3.$2.$1")}
            </span>
          </div>

          <div
            className="flex flex-row gap-2 items-center  rounded !px-1 !py-[1px] !pr-[6px]"
            style={{
              ...(review.status === "rejected" && {
                background: "rgba(230, 44, 44, 1)",
              }),
              ...(review.status === "pending" && {
                background: "rgba(218, 142, 66, 1)",
              }),
              ...(review.status === "approved" && {
                background: "rgba(43, 170, 98, 1)",
              }),
              ...(review.is_admin !== null && {
                background: "var(--global-color-link)",
              }),
            }}
          >
            {review.is_admin === null ? (
              review.status === "pending" ? (
                <IconClockCog className="w-4 h-4 [stroke:var(--global-bg-color)] shrink-0" />
              ) : review.status === "approved" ? (
                <IconBubbleText className="w-4 h-4 [stroke:var(--global-bg-color)] shrink-0" />
              ) : (
                review.status === "rejected" && (
                  <IconCancel className="w-4 h-4 [stroke:var(--global-bg-color)] shrink-0" />
                )
              )
            ) : (
              <IconCrown className="w-4 h-4 [stroke:var(--global-bg-color)] shrink-0" />
            )}
            <span className="text-[10px] [color:var(--global-bg-color)] tracking-widest">
              {review.is_admin === null &&
                (review.status === "rejected"
                  ? "Отклонен"
                  : review.status === "approved"
                    ? "Опубликован"
                    : "На модерации")}

              {review.is_admin !== null && "Администратор"}
            </span>
          </div>
        </div>

        <div className={clsx(styles.body, review.is_admin && styles.admin)}>
          <span className={clsx(styles.text, "link-font")}>{review.text}</span>
          {reply && (
            <div className={styles.reply}>
              <ReplyIcon />
              <span
                onClick={() => {
                  reviewsStore.setReply(review.id, true, review.visitor_name!);
                  const div = document.getElementById("replyForm") as HTMLDivElement;
                  div.scrollIntoView({ behavior: "smooth", block: "center" });
                }}
              >
                Ответить
              </span>
            </div>
          )}
        </div>

        {review.rating && !review.parent_id && <ReviewRate title={titleRate} value={Number(review.rating)} />}

        {review.replies?.length > 0 && (
          <div className={clsx(styles.replies, isMaxDepth && styles.max)} ref={repliesRef}>
            {isMaxDepth && <span className={styles.otherReply}>Другие ответы ветки ↓</span>}
            {!isMaxDepth && <div className={styles.line} ref={lineRef} />}
            {isMaxDepth
              ? (() => {
                  const flat = flattenReplies(review.replies);
                  const nameMap: Record<number, string | null> = {
                    [review.id]: review.visitor_name,
                  };
                  flat.forEach((r) => {
                    nameMap[r.id] = r.visitor_name;
                  });

                  return flat.map((r) => (
                    <ReviewItem
                      reply={true}
                      key={r.id}
                      review={{ ...r, replies: [] }}
                      depth={depth + 1}
                      replyToName={r.parent_id ? nameMap[r.parent_id] : undefined}
                    />
                  ));
                })()
              : review.replies.map((r: Review) => (
                  <ReviewItem reply={true} key={r.id} review={r} depth={depth + 1} />
                ))}
          </div>
        )}
      </div>
    );
  },
);
